import { Component, ChangeDetectionStrategy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-delete-confirmation-dialog',
  imports: [CommonModule],
  template: `
    @if (isOpen()) {
      <div class="dialog-overlay" (click)="close()">
        <div class="dialog-content" (click)="$event.stopPropagation()">
          <div class="dialog-header">
            <h2>Berechnung löschen?</h2>
            <button class="close-btn" (click)="close()" aria-label="Schließen">&times;</button>
          </div>
          
          <div class="dialog-body">
            <p class="warning-message">
              Möchten Sie die Berechnung <strong>"{{ calculationName() }}"</strong> wirklich löschen?
            </p>
            <p class="warning-hint">
              Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
          </div>
          
          <div class="dialog-footer">
            <button class="btn btn-secondary" (click)="close()">Abbrechen</button>
            <button 
              class="btn btn-danger" 
              (click)="confirm()"
              [disabled]="isDeleting()"
            >
              {{ isDeleting() ? 'Lösche...' : 'Löschen' }}
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .dialog-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }
    
    .dialog-content {
      background: white;
      border-radius: 8px;
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
      box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
    }
    
    .dialog-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .dialog-header h2 {
      margin: 0;
      font-size: 1.5rem;
      color: #1f2937;
    }
    
    .close-btn {
      background: none;
      border: none;
      font-size: 2rem;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    
    .close-btn:hover {
      background-color: #f3f4f6;
      color: #1f2937;
    }
    
    .dialog-body {
      padding: 1.5rem;
    }
    
    .warning-message {
      font-size: 1rem;
      color: #374151;
      margin-bottom: 1rem;
      line-height: 1.5;
    }
    
    .warning-message strong {
      color: #1f2937;
    }
    
    .warning-hint {
      background: #fef2f2;
      border-left: 3px solid #ef4444;
      padding: 0.75rem;
      border-radius: 4px;
      font-size: 0.875rem;
      color: #991b1b;
      margin: 0;
    }
    
    .dialog-footer {
      display: flex;
      gap: 0.75rem;
      justify-content: flex-end;
      padding: 1.5rem;
      border-top: 1px solid #e5e7eb;
    }
    
    .btn {
      padding: 0.75rem 1.5rem;
      border-radius: 6px;
      font-size: 1rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    
    .btn-secondary {
      background: #f3f4f6;
      color: #374151;
    }
    
    .btn-secondary:hover {
      background: #e5e7eb;
    }
    
    .btn-danger {
      background: #ef4444;
      color: white;
    }
    
    .btn-danger:hover:not(:disabled) {
      background: #dc2626;
    }
    
    .btn-danger:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeleteConfirmationDialogComponent {
  isOpen = signal(false);
  calculationName = signal('');
  isDeleting = signal(false);
  
  onConfirmed = signal<(() => void) | null>(null);
  
  /**
   * Dialog öffnen
   */
  open(calculationName: string) {
    this.calculationName.set(calculationName);
    this.isOpen.set(true);
    this.isDeleting.set(false);
  }
  
  /**
   * Dialog schließen
   */
  close() {
    this.isOpen.set(false);
    this.calculationName.set('');
    this.isDeleting.set(false);
  }
  
  /**
   * Löschen bestätigen
   */
  confirm() {
    if (this.isDeleting()) {
      return;
    }
    
    this.isDeleting.set(true);
    const callback = this.onConfirmed();
    if (callback) {
      callback();
    }
  }
}
